import { MilestoneOverrideType, PaymentRequestType } from 'types/cashFlow'
import { StudioSystemType } from 'types/global'
import { ExpectedMilestonePaymentType, PaymentOptionDataType } from 'types/paymentOptions'
import { MilestonePaymentOverridesMapType, PaymentOptionSettingsOverridesType } from 'types/systems'
import { generateUUID } from 'util/misc'
import { ResolvedSystem } from './resolveSystem'

/** Pro-edited upfront on the card is stored here until calcs/project save catch up. */
export function getDownPaymentOverrideAmount(
  system: ResolvedSystem | undefined | null,
  paymentOptionId: number
): number | undefined {
  const overrides: PaymentOptionSettingsOverridesType | undefined = system?.payment_options_settings_overrides
  if (!overrides || !(paymentOptionId in overrides)) return undefined
  const raw = overrides[paymentOptionId]?.down_payment
  if (raw === undefined || raw === null) return undefined
  const numeric = Number(raw)
  return Number.isNaN(numeric) ? undefined : numeric
}

/** First row is treated as deposit when ``is_deposit`` was not set on calc rows. */
export function findDepositMilestoneIndex(overrides: MilestoneOverrideType[]): number {
  const byFlag = overrides.findIndex((m) => m.is_deposit)
  if (byFlag >= 0) return byFlag
  if (!overrides.length) return -1
  const first = overrides[0]
  if (first.payment_number === 1 || /deposit/i.test(first.title || '')) {
    return 0
  }
  return -1
}

/** Keep a single deposit milestone and stable payment_number ordering. */
export function dedupeDepositMilestoneOverrides(overrides: MilestoneOverrideType[]): MilestoneOverrideType[] {
  if (!overrides?.length) return []

  let deposit: MilestoneOverrideType | undefined
  const rest: MilestoneOverrideType[] = []

  overrides.forEach((override) => {
    if (override.is_deposit) {
      if (!deposit) deposit = override
    } else {
      rest.push(override)
    }
  })

  const merged = deposit ? [deposit, ...rest] : rest
  return merged.map((override, index) => ({
    ...override,
    payment_number: index + 1,
  }))
}

/**
 * Apply an upfront (deposit) dollar amount to milestone overrides without creating duplicate deposits.
 */
export function applyUpfrontAmountToMilestoneOverrides(
  overrides: MilestoneOverrideType[],
  numeric: number,
  pricePayable: number,
  translate: (key: string, options?: Record<string, string>) => string
): MilestoneOverrideType[] {
  const deduped = dedupeDepositMilestoneOverrides(overrides)
  const percentage = pricePayable ? (numeric / pricePayable) * 100 : 0

  if (numeric > 0) {
    const depositIndex = findDepositMilestoneIndex(deduped)
    if (depositIndex >= 0) {
      const next = [...deduped]
      next[depositIndex] = {
        ...next[depositIndex],
        is_deposit: true,
        fixed_override_amount: numeric,
        override_percentage: percentage,
        override_uuid: next[depositIndex].override_uuid || generateUUID(),
      }
      return dedupeDepositMilestoneOverrides(next)
    }

    const newDeposit: MilestoneOverrideType = {
      is_deposit: true,
      fixed_override_amount: numeric,
      override_percentage: percentage,
      // @ts-ignore — percentage is used by calcs when override_uuid is set
      percentage,
      override_uuid: generateUUID(),
      title: translate('Deposit'),
      description: null,
      payment_number: 1,
      payment_milestone_configuration_id: null,
    }
    return dedupeDepositMilestoneOverrides([newDeposit, ...deduped])
  }

  return dedupeDepositMilestoneOverrides(deduped.filter((m) => !m.is_deposit))
}

/**
 * One row per ``payment_number``, one deposit — fixes duplicate % pills on the quote card
 * when ``milestone_payment_overrides`` or preview patches accumulated extra rows.
 */
export function dedupeExpectedMilestonePayments(
  milestones: ExpectedMilestonePaymentType[] | undefined | null
): ExpectedMilestonePaymentType[] {
  if (!milestones?.length) return []

  let deposit: ExpectedMilestonePaymentType | undefined
  const byPaymentNumber = new Map<number, ExpectedMilestonePaymentType>()
  const unnumbered: ExpectedMilestonePaymentType[] = []

  milestones.forEach((row) => {
    const isDeposit = Boolean(row.is_deposit) || /deposit/i.test(row.title || '')
    if (isDeposit) {
      if (!deposit) deposit = row
      return
    }
    const num = row.payment_number
    if (num != null && !Number.isNaN(Number(num))) {
      byPaymentNumber.set(Number(num), row)
    } else {
      unnumbered.push(row)
    }
  })

  const rest = [
    ...Array.from(byPaymentNumber.entries())
      .sort(([a], [b]) => a - b)
      .map(([, row]) => row),
    ...unnumbered,
  ]

  const merged = deposit ? [deposit, ...rest] : rest
  return merged.map((row, index) => ({
    ...row,
    payment_number: index + 1,
    ...(index === 0 && deposit ? { is_deposit: true } : {}),
  }))
}

export function milestoneOverrideHasFixedAmount(override: MilestoneOverrideType | undefined): boolean {
  if (!override) return false
  const raw = override.fixed_override_amount ?? (override as { payment_amount?: number | null }).payment_amount
  if (raw === undefined || raw === null) return false
  const numeric = Number(raw)
  return !Number.isNaN(numeric)
}

/** Map calc milestone rows into override inputs for ``getExpectedMilestones``. */
export function expectedMilestonesToOverrideInputs(
  milestones: ExpectedMilestonePaymentType[] | undefined | null
): MilestoneOverrideType[] {
  if (!milestones?.length) return []
  return milestones.map((milestone) => ({
    ...milestone,
    fixed_override_amount: milestone.payment_amount != null ? Number(milestone.payment_amount) : undefined,
  })) as unknown as MilestoneOverrideType[]
}

type AmountBearingMilestone = MilestoneOverrideType & {
  payment_amount?: number | null
  amount_requested?: number | null
  payment_milestone_override_uuid?: string | null
  percentage?: number | null
}

function coerceMilestoneAmount(row: AmountBearingMilestone): number | null {
  const amount = row.fixed_override_amount ?? row.payment_amount ?? row.amount_requested
  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) return null
  return Number(amount)
}

/**
 * Strip payment-request / calc junk so overrides stay a lean list the Adjust Payments
 * dialog and payments API can round-trip without duplicating rows (GS-9770).
 */
export function toLeanMilestoneOverrides(
  overrides: MilestoneOverrideType[] | undefined | null
): MilestoneOverrideType[] {
  if (!overrides?.length) return []
  const withAmounts = overrides.map((row) => {
    const typed = row as AmountBearingMilestone
    return {
      ...typed,
      fixed_override_amount: coerceMilestoneAmount(typed),
      payment_amount: coerceMilestoneAmount(typed) ?? typed.payment_amount,
    }
  })
  return dedupeExpectedMilestonePayments(withAmounts as unknown as ExpectedMilestonePaymentType[]).map((row, index) => {
    const typed = row as unknown as AmountBearingMilestone
    const numericAmount = coerceMilestoneAmount(typed)
    return {
      is_deposit: Boolean(typed.is_deposit) || (/deposit/i.test(typed.title || '') && index === 0),
      title: typed.title ?? null,
      description: typed.description ?? null,
      payment_number: index + 1,
      payment_milestone_configuration_id:
        typed.payment_milestone_configuration_id ?? typed.payment_milestone_configuration ?? null,
      override_uuid: typed.override_uuid ?? typed.payment_milestone_override_uuid ?? undefined,
      fixed_override_amount: numericAmount,
      override_percentage: typed.override_percentage ?? typed.percentage ?? null,
    } as MilestoneOverrideType
  })
}

/**
 * Fill missing ``fixed_override_amount`` on saved overrides from calc milestones so
 * Adjust Milestones / upfront edits never open with blank non-deposit rows.
 */
export function mergeMilestoneOverridesWithExpected(
  overrides: MilestoneOverrideType[],
  expected: ExpectedMilestonePaymentType[] | MilestoneOverrideType[] | undefined | null
): MilestoneOverrideType[] {
  const deduped = toLeanMilestoneOverrides(overrides)
  const expectedInputs = toLeanMilestoneOverrides(
    expectedMilestonesToOverrideInputs(expected as ExpectedMilestonePaymentType[] | undefined)
  )
  if (!expectedInputs.length) return deduped
  if (!deduped.length) return expectedInputs

  const merged = deduped.map((override, index) => {
    if (milestoneOverrideHasFixedAmount(override)) return override
    const byNumber = expectedInputs.find((row) => Number(row.payment_number) === Number(override.payment_number))
    const fallback = byNumber ?? expectedInputs[index]
    if (!fallback) return override
    return {
      ...fallback,
      ...override,
      fixed_override_amount:
        override.fixed_override_amount ??
        fallback.fixed_override_amount ??
        (fallback as { payment_amount?: number }).payment_amount,
      title: override.title || fallback.title,
      is_deposit: override.is_deposit ?? fallback.is_deposit,
    } as MilestoneOverrideType
  })

  // Saved overrides can be an incomplete subset of the calc milestones (e.g. only the deposit
  // + first install were persisted). Append any expected milestone that has no saved override
  // by ``payment_number`` so the dialog never silently drops a trailing milestone such as
  // "Project Complete"; existing saved rows keep their own amounts above.
  const coveredPaymentNumbers = new Set(deduped.map((override) => Number(override.payment_number)))
  const unmatchedExpected = expectedInputs.filter((row) => !coveredPaymentNumbers.has(Number(row.payment_number)))

  return toLeanMilestoneOverrides([...merged, ...unmatchedExpected])
}

/** Studio systems and serialized design ``userData`` both carry these fields. */
type HealableSystem = {
  uuid?: string
  milestone_payment_overrides?: MilestonePaymentOverridesMapType | null
  payment_options?: PaymentOptionDataType[] | null
}

export type MilestoneOverrideHealResult = {
  healed: boolean
  changedPaymentOptionIds: number[]
}

/** The keys ``toLeanMilestoneOverrides`` emits — anything else is calc or payment-request junk. */
const LEAN_MILESTONE_OVERRIDE_KEYS = new Set([
  'is_deposit',
  'title',
  'description',
  'payment_number',
  'payment_milestone_configuration_id',
  'override_uuid',
  'fixed_override_amount',
  'override_percentage',
])

/**
 * Overrides saved before GS-9770 can hold rows the payments API and timeline cannot render:
 * blank amounts, whole payment-request objects, duplicated ``payment_number``, or more rows
 * than the calc schedule. Healthy overrides are left untouched so healing never rewrites a
 * schedule a pro deliberately configured.
 */
export function milestoneOverridesNeedHealing(
  overrides: MilestoneOverrideType[] | undefined | null,
  expected?: ExpectedMilestonePaymentType[] | MilestoneOverrideType[] | null
): boolean {
  if (!overrides?.length) {
    return false
  }

  const expectedCount = expected?.length ?? 0

  if (expectedCount > 0 && overrides.length > expectedCount + 1) {
    return true
  }

  const seenPaymentNumbers = new Set<number>()

  return overrides.some((row) => {
    if (!row) {
      return true
    }

    const typed = row as AmountBearingMilestone

    if (!typed.title) return true
    if (coerceMilestoneAmount(typed) === null) return true
    if (Object.keys(row).some((key) => !LEAN_MILESTONE_OVERRIDE_KEYS.has(key))) return true

    const paymentNumber = Number(typed.payment_number)

    if (Number.isNaN(paymentNumber) || seenPaymentNumbers.has(paymentNumber)) {
      return true
    }

    seenPaymentNumbers.add(paymentNumber)

    return false
  })
}

/** ``toLeanMilestoneOverrides`` builds rows in a fixed key order, so serialization is stable. */
function milestoneOverridesAreIdentical(saved: unknown, healed: MilestoneOverrideType[]): boolean {
  try {
    return JSON.stringify(saved) === JSON.stringify(healed)
  } catch {
    return false
  }
}

/**
 * Repair ``milestone_payment_overrides`` in place for every cashflow payment option on a system,
 * using the calc milestones to fill amounts the legacy card edit left blank (GS-9851).
 */
export function healMilestonePaymentOverridesForSystem(
  system: HealableSystem | null | undefined
): MilestoneOverrideHealResult {
  const overrideMap = system?.milestone_payment_overrides as Record<string, unknown> | null | undefined

  if (!overrideMap) {
    return { healed: false, changedPaymentOptionIds: [] }
  }

  const changedPaymentOptionIds: number[] = []

  Object.keys(overrideMap).forEach((key) => {
    // The map also holds ``{paymentOptionId}_validation_overridden`` flags, which are not rows.
    const paymentOptionId = Number(key)
    if (!Number.isInteger(paymentOptionId)) return

    const saved = overrideMap[key]
    if (!Array.isArray(saved) || !saved.length) return

    const expected = system?.payment_options?.find((pmt) => pmt?.id === paymentOptionId)?.expected_milestone_payments
    if (!milestoneOverridesNeedHealing(saved as MilestoneOverrideType[], expected)) return

    const healed = mergeMilestoneOverridesWithExpected(saved as MilestoneOverrideType[], expected)
    if (!healed.length || milestoneOverridesAreIdentical(saved, healed)) return

    overrideMap[key] = healed
    changedPaymentOptionIds.push(paymentOptionId)
  })

  return { healed: changedPaymentOptionIds.length > 0, changedPaymentOptionIds }
}

/** Heal every ``OsSystem`` in a design JSON payload before Studio loads the scene. */
export function healMilestonePaymentOverridesInDesign(designJson: unknown): MilestoneOverrideHealResult {
  const children = (designJson as { object?: { children?: unknown[] } } | null)?.object?.children

  if (!Array.isArray(children)) {
    return { healed: false, changedPaymentOptionIds: [] }
  }

  const changedPaymentOptionIds = new Set<number>()

  children.forEach((child) => {
    const node = child as { type?: string; userData?: HealableSystem } | null

    if (node?.type !== 'OsSystem' || !node.userData) return

    healMilestonePaymentOverridesForSystem(node.userData).changedPaymentOptionIds.forEach((id) =>
      changedPaymentOptionIds.add(id)
    )
  })

  return { healed: changedPaymentOptionIds.size > 0, changedPaymentOptionIds: Array.from(changedPaymentOptionIds) }
}

/**
 * Build milestone override inputs for upfront edits — prefer saved overrides, then
 * issued payment requests, then the payment option's expected milestones (quote page).
 */
export function buildBaseMilestoneOverridesForPaymentOption(args: {
  system?: StudioSystemType | null
  paymentOptionId: number
  paymentOption?: PaymentOptionDataType | null
  paymentRequests?: PaymentRequestType[] | null
  /** Quote upfront: ignore bloated ``milestone_payment_overrides``; use calc milestones only. */
  useExpectedMilestonesOnly?: boolean
}): MilestoneOverrideType[] {
  const { system, paymentOptionId, paymentOption, paymentRequests, useExpectedMilestonesOnly } = args
  const expectedClean = dedupeExpectedMilestonePayments(paymentOption?.expected_milestone_payments)

  if (useExpectedMilestonesOnly) {
    return expectedMilestonesToOverrideInputs(expectedClean)
  }

  const existing = system?.milestone_payment_overrides?.[paymentOptionId]
  const existingClean = existing?.length
    ? dedupeExpectedMilestonePayments(existing as unknown as ExpectedMilestonePaymentType[])
    : []

  if (existingClean.length > 0 && existingClean.length <= expectedClean.length + 1) {
    return mergeMilestoneOverridesWithExpected(
      dedupeDepositMilestoneOverrides(existing as MilestoneOverrideType[]),
      expectedClean
    )
  }

  if (existingClean.length > expectedClean.length + 1 && expectedClean.length > 0) {
    return expectedMilestonesToOverrideInputs(expectedClean)
  }

  if (paymentRequests?.length) {
    const nonCancelledRequests = paymentRequests.filter(
      (pmtReq) => !['cancelled', 'refunded', 'chargeback complete'].includes(pmtReq.status)
    )
    const fromRequests = nonCancelledRequests
      .filter((milestone) => !milestone.is_standalone)
      .map((milestone) => {
        const fixedOverrideAmount = milestone.payment_milestone_override_uuid ? milestone.amount_requested : null
        const fixedOverridePercentage = milestone.payment_milestone_override_uuid ? milestone.percentage : null
        return {
          ...milestone,
          override_percentage: fixedOverridePercentage,
          percentage: fixedOverridePercentage,
          override_uuid: milestone.payment_milestone_override_uuid,
          fixed_override_amount: fixedOverrideAmount,
        } as MilestoneOverrideType
      })
    return dedupeDepositMilestoneOverrides(fromRequests)
  }

  return expectedMilestonesToOverrideInputs(expectedClean)
}

export function paymentOptionHasCashflowMilestones(
  paymentOption?: PaymentOptionDataType | null,
  paymentRequests?: PaymentRequestType[] | null,
  system?: StudioSystemType | null,
  paymentOptionId?: number
): boolean {
  if (paymentRequests?.length) return true
  if ((paymentOption?.expected_milestone_payments ?? []).length > 0) return true
  if (paymentOptionId != null && (system?.milestone_payment_overrides?.[paymentOptionId] ?? []).length > 0) {
    return true
  }
  return false
}
